import { minutesToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Button, Link, Modal, ModalBody, Subhead } from '@growtherapy/sprout-ui';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useGetTelehealthSessionInfo } from '../../hooks/useGetTelehealthSessionInfo';
import { Translate } from '../../i18n';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { providerConsentAtom } from '../../session/messages/state';
import { ClientTranscriptionUserAction } from '../../session/types';
import { sessionInfoAtom } from '../../state';
import { toast } from '../../toasts';
import { UserType } from '../../types';
import { ConsentStatus, participantCountAtom } from '../../video-sdk';
import { useSendVisitorParticipantStateCallback } from '../messages/useSendVisitorParticipantState';
import { AI_FEATURES_FAQ_LINK } from './constants';
import { useGracefullyRecordClientTranscriptionConsentCallback } from './useGracefullyRecordClientTranscriptionConsentCallback';
import { useShowRefreshedClientConsentCopy } from './useShowRefreshedClientConsentCopy';
import { useToggleRecordingCallback } from './useToggleRecordingCallback';

export type ConsentModalProps = {
  onDeclineConsent: () => void;
  onConsent: () => Promise<void>;
  onDismissConsent: () => void;
  isLoading: boolean;
  isOpen: boolean;
  autoDismissTimeout?: number;
  providerName: string;
  isMultiPatientSession: boolean;
  showRefreshedCopy?: boolean;
};

export function ConsentModal({
  onDeclineConsent,
  onConsent,
  onDismissConsent,
  isLoading,
  isOpen,
  providerName,
  isMultiPatientSession,
  showRefreshedCopy = false,
  autoDismissTimeout = minutesToMilliseconds(2),
}: ConsentModalProps) {
  const t = useTranslate();
  const consentButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(
    function dismissAfterTwoMinutes() {
      if (!isOpen) return;
      const timeout = setTimeout(function dismissModal() {
        onDismissConsent();
      }, autoDismissTimeout);
      return function cleanup() {
        clearTimeout(timeout);
      };
    },
    [isOpen, autoDismissTimeout, onDismissConsent],
  );

  useEffect(
    function focusConsentButtonOnOpen() {
      if (!isOpen || !showRefreshedCopy) return;
      const frame = requestAnimationFrame(() => consentButtonRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    },
    [isOpen, showRefreshedCopy],
  );

  return (
    <Modal
      onClose={onDismissConsent}
      isOpen={isOpen}
      className='shadow-none sm:max-w-[31rem] md:max-w-[31rem] sm:top-[25%] rebrand:bg-surface-inverse'
      onOutsideClick={onDismissConsent}
    >
      <ModalBody className='flex flex-col items-center gap-4 p-8 text-center'>
        <div className='flex flex-col gap-2 items-center'>
          <Subhead.Default as='h2' className='rebrand:text-text-inverse'>
            {showRefreshedCopy
              ? t('telehealth-core:consent.transcriptionConsent')
              : `${providerName} ${t('telehealth-core:consent.wouldLikeToUseGrowTherapysCapability')}`}
          </Subhead.Default>
        </div>
        <div
          className={
            showRefreshedCopy ? 'w-full flex flex-col gap-3 text-left' : 'w-full flex flex-col items-center text-center'
          }
        >
          {isMultiPatientSession && (
            <Body.Small className='rebrand:text-text-inverse'>
              {t('telehealth-core:consent.consentWillApplyToAllParticipants')}
            </Body.Small>
          )}
          {showRefreshedCopy ? (
            <Body.Small className='rebrand:text-text-inverse'>
              <Translate
                i18nKey='telehealth-core:consent.growUsesAiTranscriptionToAidWithLink'
                components={{
                  1: <Link className='font-normal rebrand:text-text-inverse' external href={AI_FEATURES_FAQ_LINK} />,
                }}
              />
            </Body.Small>
          ) : (
            <Body.Small className='rebrand:text-text-inverse'>
              {t('telehealth-core:consent.youCanChangeThisSettingAtAnyTime')}
            </Body.Small>
          )}
        </div>
        <div className='flex flex-col sm:flex-row gap-4 w-full'>
          <Button
            disabled={isLoading}
            onClick={onDeclineConsent}
            use='primary'
            className='w-full rebrand:border-border-default'
          >
            {showRefreshedCopy
              ? t('telehealth-core:consent.noIDoNotConsentToTranscription')
              : t('telehealth-core:consent.noIDontConsent')}
          </Button>
          <Button ref={consentButtonRef} loading={isLoading} onClick={onConsent} use='secondary' className='w-full m-0'>
            {showRefreshedCopy
              ? t('telehealth-core:consent.yesIConsentToTranscription')
              : t('telehealth-core:consent.yesIConsent')}
          </Button>
        </div>
        {showRefreshedCopy && (
          <Body.Xsmall className='rebrand:text-text-inverse w-full text-left'>
            <Translate
              i18nKey='telehealth-core:consent.pleaseBeAwareThatThisTechnologyCanMakeMistakesWithLink'
              components={{
                1: <Link className='font-normal rebrand:text-text-inverse' external href={AI_FEATURES_FAQ_LINK} />,
              }}
            />
          </Body.Xsmall>
        )}
      </ModalBody>
    </Modal>
  );
}

enum RecordConsentResponse {
  'CONSENTED',
  'ERROR',
  'CONSENTED_AND_RECORDING',
}

function useRecordConsentCallback() {
  const t = useTranslate();
  const { userClicked } = useTrackEvent();
  const getSessionInfo = useAtomCallbackMemoized((get) => get(sessionInfoAtom), []);
  const getProviderConsent = useAtomCallbackMemoized((get) => get(providerConsentAtom).value, []);
  const toggleRecording = useToggleRecordingCallback();
  const { recordClientConsentCallback: recordConsent, isLoading } =
    useGracefullyRecordClientTranscriptionConsentCallback();

  const recordConsentCallback = useCallback(
    async (isOptedIn: boolean, userAction?: ClientTranscriptionUserAction) => {
      const sessionInfo = getSessionInfo();
      const appointmentShortId = sessionInfo?.appointment?.shortId;
      userClicked(EventTypeEnum.GAVE_TRANSCRIPTION_CONSENT, {
        appointmentShortId: appointmentShortId!,
        entityType: UserType.CLIENT,
      });

      const recordingResult = await recordConsent(isOptedIn, userAction);

      if (recordingResult.status === 'failure') {
        toast.error(t('telehealth-core:consent.anUnexpectedErrorOccurredWhileTryingToRecordYourConsent'));
        return RecordConsentResponse.ERROR;
      }

      if (recordingResult.status === 'success' && isOptedIn) {
        // Start recording if provider has consented
        const providerConsent = getProviderConsent();
        if (providerConsent === ConsentStatus.OPTED_IN) {
          toggleRecording(true);
          return RecordConsentResponse.CONSENTED_AND_RECORDING;
        }
      }

      return RecordConsentResponse.CONSENTED;
    },
    [getProviderConsent, getSessionInfo, recordConsent, t, toggleRecording, userClicked],
  );

  return {
    recordConsentCallback,
    isLoading,
  };
}

export function ConsentModalWrapper({
  onClose,
  onStartRecording,
  setShouldShowClientConsentDeclinedToast,
  isOpen,
  setPatientConsent,
}: {
  onClose: () => void;
  onStartRecording: () => void;
  setShouldShowClientConsentDeclinedToast: (shouldShow: boolean) => void;
  isOpen: boolean;
  setPatientConsent: ({ value, lastUpdated }: { value: ConsentStatus; lastUpdated: number }) => void;
}) {
  const t = useTranslate();
  const participantCount = useAtomValue(participantCountAtom);
  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const providerName = data?.telehealthSessionInfo?.provider?.name ?? t('telehealth-core:consent.yourProvider');
  const showRefreshedCopy = useShowRefreshedClientConsentCopy(patientShortId);
  const sendParticipantState = useSendVisitorParticipantStateCallback();

  const { recordConsentCallback: recordConsent, isLoading } = useRecordConsentCallback();
  const onConsent = useCallback(async () => {
    recordConsent(true).then((consentResult) => {
      if (consentResult === RecordConsentResponse.CONSENTED_AND_RECORDING) {
        onStartRecording();
      }
      if (consentResult !== RecordConsentResponse.ERROR) {
        onClose();
      }
    });
  }, [recordConsent, onStartRecording, onClose]);

  // Dismiss modal and set consent to SOFT_NO and show toast
  const declineConsent = useCallback(() => {
    setPatientConsent({
      value: ConsentStatus.SOFT_NO,
      lastUpdated: Date.now(),
    });
    recordConsent(false, ClientTranscriptionUserAction.REJECT).then((consentResult) => {
      if (consentResult !== RecordConsentResponse.ERROR) {
        sendParticipantState();
        setShouldShowClientConsentDeclinedToast(true);
        onClose();
      }
    });
  }, [sendParticipantState, setPatientConsent, recordConsent, setShouldShowClientConsentDeclinedToast, onClose]);

  // Dismiss modal and set consent to SOFT_NO WITHOUT showing toast
  const dismissConsent = useCallback(() => {
    // Guard against this being called if the modal is not open, which
    // technically may happen if the Sprout modal is closed but still computed
    // to render with its internal state.
    if (!isOpen) return;
    setPatientConsent({
      value: ConsentStatus.SOFT_NO,
      lastUpdated: Date.now(),
    });
    recordConsent(false, ClientTranscriptionUserAction.DISMISS).then((consentResult) => {
      if (consentResult !== RecordConsentResponse.ERROR) {
        sendParticipantState();
        setShouldShowClientConsentDeclinedToast(true);
        onClose();
      }
    });
  }, [
    sendParticipantState,
    setPatientConsent,
    recordConsent,
    setShouldShowClientConsentDeclinedToast,
    onClose,
    isOpen,
  ]);

  return (
    <ConsentModal
      onDismissConsent={dismissConsent}
      onDeclineConsent={declineConsent}
      isLoading={isLoading}
      isOpen={isOpen}
      providerName={providerName}
      isMultiPatientSession={participantCount > 1}
      showRefreshedCopy={showRefreshedCopy}
      onConsent={onConsent}
    />
  );
}
